import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Chart } from '@frontend/charts';
import { formatDate } from '@frontend/date';
import { i18next, useTranslation } from '@frontend/i18n';
import {
  useGetSchedulePulseInsuranceTabAppointments,
  useGetInsuranceStatuses,
  StatusSelectionLabel,
} from '@frontend/insurance-tab';
import { theme } from '@frontend/theme';
import { useSchedulePulseInsuranceTabContext } from '../../context';
import { LocationAccordionList } from '../LocationAccordionList';
import { InsuranceSpinnerLoader } from './InsuranceSpinnerLoader';
import { InsuranceTabDateHeader } from './InsuranceTabDateHeader';
import { VerificationAnalysisPopoverTooltip } from './VerificationAnalysisPopoverTooltip';

const colors = {
  autoVerified: theme.colors.success30,
  manualVerified: theme.colors.primary40,
  error: theme.colors.critical50,
  unverified: theme.colors.warning30,
};

const labels = {
  autoVerified: i18next.t('Auto-Verified', { ns: 'scheduleCalendarActions' }),
  manualVerified: i18next.t('Manual Verified', { ns: 'scheduleCalendarActions' }),
  error: i18next.t('Error', { ns: 'scheduleCalendarActions' }),
  unverified: i18next.t('Unverified', { ns: 'scheduleCalendarActions' }),
  total: i18next.t('Total', { ns: 'scheduleCalendarActions' }),
};

export const VerificationAnalysis = () => {
  const { selectedLocationIds } = useSchedulePulseInsuranceTabContext();

  const isMultiLocation = selectedLocationIds.length > 1;

  return (
    <section>
      <div>
        <InsuranceTabDateHeader type='analytics' />
      </div>
      <div css={{ padding: theme.spacing(3) }}>
        <LocationAccordionList
          minBodyHeight={80}
          ContentComponent={SingleLocationChartView}
          isMultiLocation={isMultiLocation}
          style={css({
            ...(!isMultiLocation && { borderRadius: theme.borderRadius.medium }),
          })}
        />
      </div>
    </section>
  );
};

interface SingleLocationChartViewProps {
  locationId: string;
  height?: number | string;
}

const SingleLocationChartView = ({ locationId }: SingleLocationChartViewProps) => {
  const { t } = useTranslation('scheduleCalendarActions');

  const { selectedDate, selectedLocationIds } = useSchedulePulseInsuranceTabContext();

  const { allAppointmentsData, isLoadingAppointmentsData } = useGetSchedulePulseInsuranceTabAppointments({
    selectedDate: selectedDate || formatDate(new Date(), 'MM/DD/YYYY'),
    locationIds: [locationId],
  });

  const { isLoading, insuranceStatuses } = useGetInsuranceStatuses({
    locationId: locationId,
    appointments: allAppointmentsData || [],
    isSingleLocationView: true,
  });

  const isMultiLocation = selectedLocationIds.length > 1;

  const { autoVerified, manualVerified, error, unverified, total } = useMemo(() => {
    return {
      autoVerified:
        insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis?.[
          StatusSelectionLabel.AutoVerified
        ] || 0,
      manualVerified:
        insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis?.[
          StatusSelectionLabel.ManualVerified
        ] || 0,
      error:
        insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis?.[
          StatusSelectionLabel.Error
        ] || 0,
      unverified:
        insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis?.[
          StatusSelectionLabel.Unverified
        ] || 0,
      total:
        insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis?.[
          StatusSelectionLabel.Total
        ] || 0,
    };
  }, [insuranceStatuses?.locationVerificationCounts, locationId]);

  if (isLoading || isLoadingAppointmentsData) {
    return <InsuranceSpinnerLoader />;
  }

  return (
    <div css={chartContainerStyles}>
      <Chart
        colors={colors}
        commonTooltipLabel={t('Verification Analysis')}
        isLoading={isLoading}
        labels={labels}
        noBorder={isMultiLocation}
        style={{ marginBottom: 0, ...(isMultiLocation && { padding: theme.spacing(3) }) }}
      >
        <Chart.Header rightElement={<VerificationAnalysisPopoverTooltip />} title={t('Verification Analysis')} />
        <Chart.PieChart
          appearance={{
            width: 240,
            height: 240,
            innerRadius: 55,
            outerRadius: 95,
          }}
          data={{
            centerMetric: total,
            centerMetricLabel: t('Total'),
            groups: [
              {
                name: 'autoVerified',
                value: autoVerified,
              },
              {
                name: 'manualVerified',
                value: manualVerified,
              },
              {
                name: 'error',
                value: error,
              },
              {
                name: 'unverified',
                value: unverified,
              },
            ],
          }}
        />

        <Chart.Legends
          values={{
            autoVerified,
            manualVerified,
            error,
            unverified,
          }}
        />
      </Chart>
    </div>
  );
};

const chartContainerStyles = css({
  backgroundColor: theme.colors.white,
  height: '100%',
});
